import React from 'react';
import { TrendingUp, Trophy, Star, Filter, BarChart3 } from 'lucide-react';
import './TopItemChart.css';

function TopItemChart({ orders, timeFilter, onTimeFilterChange }) {
  const getFilteredData = (filter) => {
    const now = new Date();
    const filteredOrders = orders.filter(order => {
      if (!order.createdAt) return false;
      const orderDate = order.createdAt.toDate();
      
      switch (filter) {
        case 'daily':
          return orderDate.toDateString() === now.toDateString();
        case 'weekly':
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          return orderDate >= weekAgo;
        case 'monthly':
          return orderDate.getMonth() === now.getMonth() && 
                 orderDate.getFullYear() === now.getFullYear();
        case 'yearly':
          return orderDate.getFullYear() === now.getFullYear();
        default:
          return true;
      }
    });

    return filteredOrders;
  };

  const getTopProducts = () => {
    const filteredOrders = getFilteredData(timeFilter);
    const productSales = {};
    const productRevenue = {};
    
    filteredOrders.forEach(order => {
      order.items?.forEach(item => {
        productSales[item.name] = (productSales[item.name] || 0) + item.quantity;
        productRevenue[item.name] = (productRevenue[item.name] || 0) + (item.price * item.quantity);
      });
    });

    return Object.entries(productSales)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 10)
      .map(([name, quantity]) => ({
        name,
        quantity,
        revenue: productRevenue[name] || 0
      }));
  };

  const getCategoryPerformance = () => {
    const filteredOrders = getFilteredData(timeFilter);
    const categoryData = {};
    
    filteredOrders.forEach(order => {
      order.items?.forEach(item => {
        const category = item.category || 'Uncategorized';
        if (!categoryData[category]) {
          categoryData[category] = { quantity: 0, revenue: 0, products: new Set() };
        }
        categoryData[category].quantity += item.quantity;
        categoryData[category].revenue += item.price * item.quantity;
        categoryData[category].products.add(item.name);
      });
    });

    return Object.entries(categoryData)
      .map(([category, data]) => ({
        category,
        quantity: data.quantity,
        revenue: data.revenue,
        productCount: data.products.size
      }))
      .sort((a, b) => b.revenue - a.revenue);
  };

  const topProducts = getTopProducts();
  const categoryPerformance = getCategoryPerformance();
  const maxQuantity = Math.max(...topProducts.map(p => p.quantity));

  return (
    <div className="top-item-chart">
      <div className="chart-header">
        <h3>Top Selling Products</h3>
        <div className="chart-time-filter">
          <Filter size={16} />
          <select value={timeFilter} onChange={(e) => onTimeFilterChange(e.target.value)}>
            <option value="daily">Today</option>
            <option value="weekly">This Week</option>
            <option value="monthly">This Month</option>
            <option value="yearly">This Year</option>
          </select>
        </div>
      </div>

      <div className="top-products-showcase">
        {topProducts.slice(0, 3).map((product, index) => (
          <div key={product.name} className={`top-product-card rank-${index + 1}`}>
            <div className="rank-badge">
              {index === 0 ? <Trophy size={20} /> : <span>#{index + 1}</span>}
            </div>
            <div className="product-info">
              <h4>{product.name}</h4>
              <div className="product-stats">
                <span className="quantity">{product.quantity} sold</span>
                <span className="revenue">${product.revenue.toFixed(2)}</span>
              </div>
            </div>
            <div className="performance-indicator">
              <div className="progress-ring">
                <svg width="60" height="60">
                  <circle
                    cx="30"
                    cy="30"
                    r="25"
                    stroke="#e5e7eb"
                    strokeWidth="4"
                    fill="none"
                  />
                  <circle
                    cx="30"
                    cy="30"
                    r="25"
                    stroke="url(#gradient)"
                    strokeWidth="4"
                    fill="none"
                    strokeDasharray={`${(product.quantity / maxQuantity) * 157} 157`}
                    strokeDashoffset="0"
                    transform="rotate(-90 30 30)"
                  />
                  <defs>
                    <linearGradient id="gradient">
                      <stop offset="0%" stopColor="#dc2626" />
                      <stop offset="100%" stopColor="#000000" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="ring-text">
                  <span>{Math.round((product.quantity / maxQuantity) * 100)}%</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="detailed-rankings">
        <h4>Complete Rankings</h4>
        <div className="ranking-list">
          {topProducts.map((product, index) => (
            <div key={product.name} className="ranking-item">
              <div className="ranking-position">
                <span className="position-number">{index + 1}</span>
                {index < 3 && <Star size={12} className="star-icon" />}
              </div>
              <div className="ranking-details">
                <div className="product-name">{product.name}</div>
                <div className="product-metrics">
                  <span className="metric">{product.quantity} units</span>
                  <span className="metric revenue">${product.revenue.toFixed(2)}</span>
                  <span className="metric avg">${product.revenue > 0 ? (product.revenue / product.quantity).toFixed(2) : '0.00'}/unit</span>
                </div>
              </div>
              <div className="ranking-bar">
                <div 
                  className="bar-fill" 
                  style={{ 
                    width: `${(product.quantity / maxQuantity) * 100}%`,
                    background: index < 3 
                      ? 'linear-gradient(135deg, #dc2626 0%, #000000 100%)' 
                      : 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)'
                  }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="category-performance">
        <h4>Category Performance</h4>
        <div className="category-grid">
          {categoryPerformance.slice(0, 4).map((cat, index) => (
            <div key={cat.category} className="category-card">
              <div className="category-icon">
                <BarChart3 size={24} />
              </div>
              <div className="category-info">
                <h5>{cat.category}</h5>
                <div className="category-stats">
                  <span>{cat.quantity} items</span>
                  <span>${cat.revenue.toFixed(2)}</span>
                </div>
                <div className="product-count">{cat.productCount} products</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default TopItemChart;
